import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DotPlotCell } from "./DotPlotCell";
import { CandidateDetailDialog } from "./CandidateDetailDialog";
import {
  MOCK_CANDIDATES,
  MOCK_EVALUATORS,
  CATEGORY_NAMES,
  CRITERIA_GROUPS,
  compositeScore,
  candidateDisagreementScore,
  DISAGREEMENT_THRESHOLD,
  type Candidate,
  type ScoreMap,
  type CriteriaCategory,
} from "./mockSelectionData";

type SortKey = "composite" | "disagreement" | "name";

interface Props {
  candidates: Candidate[];
  scores: ScoreMap;
}

// One representative CriteriaCategory per category name (for color)
const CATEGORY_META: Record<string, CriteriaCategory> = {};
CRITERIA_GROUPS.forEach((g: CriteriaCategory) => {
  if (!CATEGORY_META[g.category]) CATEGORY_META[g.category] = g;
});

function avgScoresForCategory(
  candidateId: number,
  category: string,
  scores: ScoreMap
): number[] {
  const groups = CRITERIA_GROUPS.filter((g) => g.category === category);
  const keys = groups.flatMap((g) => g.criteria.map((c) => c.key));
  return MOCK_EVALUATORS.map((ev) => {
    const vals = keys.map((k) => scores[candidateId][ev][k]);
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  });
}

export function PanelComparisonTab({ scores, candidates: propCandidates }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("composite");
  const [filterDisagreement, setFilterDisagreement] = useState(false);
  const [dialogCandidate, setDialogCandidate] = useState<Candidate | null>(null);

  const candidates = propCandidates.filter((c) =>
    filterDisagreement
      ? candidateDisagreementScore(c.id) > DISAGREEMENT_THRESHOLD
      : true
  ).sort((a, b) => {
    if (sortKey === "name") return a.name.localeCompare(b.name);
    if (sortKey === "disagreement")
      return candidateDisagreementScore(b.id) - candidateDisagreementScore(a.id);
    return compositeScore(b.id) - compositeScore(a.id);
  });

  return (
    <div className="space-y-4" data-testid="panel-comparison-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 shrink-0">Sort:</span>
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger className="w-52" data-testid="sort-dropdown">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="composite">By Composite Score</SelectItem>
              <SelectItem value="disagreement">By Disagreement</SelectItem>
              <SelectItem value="name">By Name</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant={filterDisagreement ? "default" : "outline"}
          size="sm"
          className={filterDisagreement ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
          onClick={() => setFilterDisagreement((v) => !v)}
          data-testid="toggle-disagreement-filter"
        >
          {filterDisagreement ? "Showing disagreement only" : "Show only disagreement candidates"}
        </Button>

        <span className="text-xs text-gray-400 ml-auto">
          Variance &gt; {DISAGREEMENT_THRESHOLD} flagged
        </span>
      </div>

      {/* Main table */}
      <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="comparison-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-44 font-semibold">Candidate</TableHead>
              <TableHead className="w-20 font-semibold text-center">Score</TableHead>
              {CATEGORY_NAMES.map((cat) => (
                <TableHead key={cat} className="font-semibold text-center min-w-[80px]">
                  {cat}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {candidates.map((candidate) => {
              const composite = compositeScore(candidate.id);
              const disagreement = candidateDisagreementScore(candidate.id);
              const hasDisagreement = disagreement > DISAGREEMENT_THRESHOLD;

              return (
                <TableRow
                  key={candidate.id}
                  className={hasDisagreement ? "bg-amber-50/50" : undefined}
                  data-testid={`comparison-row-${candidate.id}`}
                >
                  {/* Candidate name cell */}
                  <TableCell className="py-3">
                    <button
                      className="text-left"
                      onClick={() => setDialogCandidate(candidate)}
                      data-testid={`candidate-name-btn-${candidate.id}`}
                    >
                      <p className="text-sm font-medium text-blue-700 hover:underline">
                        {candidate.name}
                      </p>
                      <p className="text-xs text-gray-400">{candidate.position}</p>
                    </button>
                    {hasDisagreement && (
                      <Badge
                        className="mt-1 bg-amber-100 text-amber-700 border border-amber-300 text-xs font-normal"
                        data-testid={`disagreement-badge-${candidate.id}`}
                      >
                        ⚠ disagreement
                      </Badge>
                    )}
                  </TableCell>

                  {/* Composite score */}
                  <TableCell className="text-center py-3">
                    <span className="text-sm font-bold text-gray-800">
                      {composite.toFixed(2)}
                    </span>
                  </TableCell>

                  {/* Category dot-plot columns */}
                  {CATEGORY_NAMES.map((cat) => {
                    const meta = CATEGORY_META[cat];
                    const catScores = avgScoresForCategory(candidate.id, cat, scores);
                    const rounded = catScores.map((s) => Math.round(s));

                    return (
                      <TableCell
                        key={cat}
                        className="text-center py-3"
                        data-testid={`cell-${candidate.id}-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <DotPlotCell scores={rounded} color={meta.color} />
                          <span className="text-[10px] text-gray-400">
                            {catScores.map((s) => s.toFixed(1)).join(" · ")}
                          </span>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {CATEGORY_NAMES.map((cat) => {
          const meta = CATEGORY_META[cat];
          const colorDot: Record<CriteriaCategory["color"], string> = {
            blue: "bg-blue-500",
            green: "bg-green-600",
            purple: "bg-purple-600",
            amber: "bg-amber-500",
          };
          return (
            <div key={cat} className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${colorDot[meta.color]}`} />
              {cat}
            </div>
          );
        })}
        <div className="flex items-center gap-1 ml-2">
          <span className="text-amber-500">⚠</span>
          avg criterion variance &gt; {DISAGREEMENT_THRESHOLD}
        </div>
      </div>

      {/* Candidate Detail Dialog */}
      <CandidateDetailDialog
        candidate={dialogCandidate}
        scores={scores}
        open={dialogCandidate !== null}
        onClose={() => setDialogCandidate(null)}
      />
    </div>
  );
}
